import { useState } from "react";
import {
    Popover,
    PopoverHandler,
    PopoverContent,
    Typography,
    IconButton,
} from "@material-tailwind/react";
import {
    DocumentTextIcon,
    EyeIcon,
    ExclamationTriangleIcon,
    CheckBadgeIcon,
} from "@heroicons/react/24/solid";
import moment from "moment";
import DocumentViewModal from "@/components/DocumentViewModal";
import DownloadFile from "@/components/DownloadFile";
import { useTranslation } from "react-i18next";
export default function RfqPurchaseOrderPopover({ purchase_order_data, all_purchase_orders = [], buttonText = "Purchase Orders" }) {
    const [open, setOpen] = useState(false);
    const [isSetPurchaseOrderFile, setPurchaseOrderFile] = useState(false);
    const { t } = useTranslation('rfq');

    return (
        <>
            <Popover open={open} handler={setOpen} placement="bottom-end">
                <PopoverHandler>
                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all shadow-sm bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900">
                        <EyeIcon className="h-4 w-4 text-gray-600" />
                        {buttonText} {all_purchase_orders?.length > 0 && `(${all_purchase_orders.length})`}
                    </button>
                </PopoverHandler>

                <PopoverContent className={`w-96 p-0 overflow-hidden rounded-2xl shadow-xl bg-white border border-gray-200 ${
                    isSetPurchaseOrderFile ? "z-[40]" : "z-[999]"
                }`}>
                    {/* Header */}
                    <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                        <Typography variant="h6" className="font-bold text-gray-900">
                            {t('purchase_order_history_title')}
                        </Typography>
                        <Typography className="text-[11px] text-gray-500 font-medium">
                            {t('purchase_order_history_desc')}
                        </Typography>
                    </div>

                    {/* Scrollable Container - max-h prevents UI distortion with 10+ files */}
                    <div className="max-h-[450px] overflow-y-auto p-5 space-y-5 custom-scrollbar">
                        {!all_purchase_orders || all_purchase_orders.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
                                <ExclamationTriangleIcon className="h-10 w-10 text-gray-300" />
                                <Typography className="text-sm font-medium text-gray-500">
                                    {t('no_purchase_orders_available')}
                                </Typography>
                            </div>
                        ) : (
                            all_purchase_orders.map((po, index) => {
                                // Identify latest by matching the current PO file and its upload time.
                                const isLatest = po.purchase_order_file?.url === purchase_order_data?.url &&
                                    po.purchase_order_file?.uploaded_at === purchase_order_data?.uploaded_at;

                                return (
                                    <div key={po.id || index} className="group animate-fadeIn">
                                        <div className="flex justify-between items-center mb-2">
                                            <div className="flex items-center gap-2">
                                                <Typography className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                                    isLatest ? "text-green-700 bg-green-100" : "text-blue-600 bg-blue-50"
                                                }`}>
                                                    {t('purchase_order_number_label')}{po.purchase_order_number || t('na')}
                                                </Typography>
                                                {isLatest && (
                                                    <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 uppercase">
                                                        <CheckBadgeIcon className="h-3 w-3" />
                                                        {t('latest_label')}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-gray-400 font-medium">
                                                {moment(po.purchase_order_file?.uploaded_at).format("MMM DD, YYYY hh:mm A")}
                                            </span>
                                        </div>

                                        <div className={`rounded-xl border transition-all duration-300 ${
                                            isLatest 
                                            ? "bg-green-50/30 border-green-200 shadow-sm" 
                                            : "bg-gray-50 border-gray-200 group-hover:border-gray-300"
                                        } p-3`}>
                                            <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-gray-100 group-hover:border-blue-300 transition-all">
                                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                                    <DocumentTextIcon className={`h-5 w-5 shrink-0 ${isLatest ? 'text-green-500' : 'text-blue-500'}`} />
                                                    <Typography className="text-xs font-semibold text-gray-700 truncate">
                                                        {po.purchase_order_file?.file_name}
                                                    </Typography>
                                                </div>
                                                
                                                <div className="flex items-center gap-1">
                                                    <IconButton 
                                                        variant="text" 
                                                        size="sm" 
                                                        color={isLatest ? "green" : "blue"}
                                                        onClick={() => setPurchaseOrderFile(po.purchase_order_file?.url)}
                                                        className="rounded-full"
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </IconButton>
                                                    
                                                    {po.purchase_order_file?.url && (
                                                        <DownloadFile fileUrl={po.purchase_order_file.url} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    
                    {/* Footer shadow indicator */}
                    <div className="h-2 bg-gradient-to-t from-gray-50 to-transparent"></div>
                </PopoverContent>
            </Popover>

            {isSetPurchaseOrderFile && (
                <DocumentViewModal
                    documentUrl={isSetPurchaseOrderFile}
                    setIsDocumentViewModalOpen={setPurchaseOrderFile}
                />
            )}
        </>
    );
}
